import { BarChart3, CalendarRange, Percent, RefreshCw, Target } from 'lucide-react';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { useState } from 'react';

import {
  usePerformanceDrilldown,
  usePerformanceHeatmap,
  useCalculatePerformance,
} from '@/features/coupon-performance';
import { testIds } from '@/shared/testing';
import { AnalysisGuide } from '@/shared/ui/analysis-guide';
import { AnimatedContainer } from '@/shared/ui/animated';
import { AnimatedCount } from '@/shared/ui/animated-count';
import { MemberOnlyNotice } from '@/shared/ui/member-only-notice';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent } from '@/shared/ui/card';
import { EmptyState } from '@/shared/ui/empty-state';
import { PageHeader } from '@/shared/ui/page-header';
import { Skeleton } from '@/shared/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { AppLayout } from '../../-components/app-layout';

import type { HeatmapRow } from '@cafe24-apps/traffic-light-server/contracts';

export const Route = createFileRoute('/_authenticated/$mallId/analysis/coupon-performance/')({
  component: CouponPerformancePage,
});

function getHeatmapColor(rate: number): string {
  if (rate === 0) return 'bg-muted text-muted-foreground';
  if (rate < 10) return 'bg-chart-5 text-primary';
  if (rate < 25) return 'bg-chart-4 text-primary';
  if (rate < 50) return 'bg-chart-3 text-primary-foreground';
  return 'bg-chart-2 text-primary-foreground';
}

// Group flat heatmap rows by issuedMonth
type GroupedHeatmapRow = {
  issuedMonth: string;
  maxIssuedCount: number;
  cells: Map<number, HeatmapRow>;
};

function groupHeatmapRows(rows: HeatmapRow[]): GroupedHeatmapRow[] {
  const map = new Map<string, GroupedHeatmapRow>();
  for (const row of rows) {
    if (!map.has(row.issuedMonth)) {
      map.set(row.issuedMonth, {
        issuedMonth: row.issuedMonth,
        maxIssuedCount: 0,
        cells: new Map(),
      });
    }
    const group = map.get(row.issuedMonth)!;
    group.cells.set(row.monthOffset, row);
    if (row.issuedCount > group.maxIssuedCount) {
      group.maxIssuedCount = row.issuedCount;
    }
  }
  return Array.from(map.values()).sort((a, b) => b.issuedMonth.localeCompare(a.issuedMonth));
}

function PerformanceHeatmap({
  rows,
  maxMonthOffset,
  isLoading,
  selectedMonth,
  onSelectMonth,
}: {
  rows: HeatmapRow[];
  maxMonthOffset: number;
  isLoading: boolean;
  selectedMonth: string | null;
  onSelectMonth: (month: string) => void;
}) {
  const grouped = groupHeatmapRows(rows);
  const offsets = Array.from({ length: maxMonthOffset + 1 }, (_, i) => i);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (grouped.length === 0) {
    return (
      <EmptyState
        icon={BarChart3}
        title="아직 성과 데이터가 없어요"
        description="상단의 재계산 버튼을 눌러 성과를 계산해 보세요"
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table
        className="w-full text-xs border-collapse"
        data-testid={testIds.couponPerformance.heatmapTable}
      >
        <thead>
          <tr>
            <th className="text-left px-3 py-2 font-medium text-muted-foreground whitespace-nowrap min-w-[140px]">
              발급 월
            </th>
            <th className="px-3 py-2 font-medium text-muted-foreground whitespace-nowrap text-right min-w-[80px]">
              발급 수
            </th>
            {offsets.map((offset) => (
              <th
                key={offset}
                className="px-2 py-2 font-medium text-muted-foreground whitespace-nowrap text-center min-w-[56px]"
              >
                M+{offset}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grouped.map((group) => {
            const isSelected = selectedMonth === group.issuedMonth;
            return (
              <tr
                key={group.issuedMonth}
                className={`border-t border-border cursor-pointer transition-colors ${
                  isSelected ? 'bg-accent' : 'hover:bg-muted/50'
                }`}
                data-testid={testIds.couponPerformance.heatmapRow}
                onClick={() => onSelectMonth(group.issuedMonth)}
              >
                <td className="px-3 py-2 font-medium whitespace-nowrap">{group.issuedMonth}</td>
                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                  {group.maxIssuedCount.toLocaleString('ko-KR')}
                </td>
                {offsets.map((offset) => {
                  const cell = group.cells.get(offset);
                  if (!cell) {
                    return (
                      <td key={offset} className="px-2 py-2 text-center">
                        <div className="rounded-lg px-1.5 py-1 bg-muted text-muted-foreground text-center">
                          —
                        </div>
                      </td>
                    );
                  }
                  return (
                    <td key={offset} className="px-2 py-2 text-center">
                      <div
                        className={`rounded-lg px-1.5 py-1 tabular-nums font-medium text-center ${getHeatmapColor(cell.repurchaseRate)}`}
                        title={`재구매: ${cell.repurchasedCount}명`}
                      >
                        {cell.repurchaseRate.toFixed(1)}%
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const SOURCE_LABELS: Record<string, string> = {
  rfm: 'RFM',
  cohort: '코호트',
  ltv: 'LTV',
  manual: '수동',
  purchase_cycle: '구매주기',
  automation: '자동화',
};

function DrilldownTable({ issuedMonth }: { issuedMonth: string }) {
  const { mallId } = useParams({ from: '/_authenticated/$mallId/analysis/coupon-performance/' });
  const { data, isLoading } = usePerformanceDrilldown(issuedMonth);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (!data || data.events.length === 0) {
    return (
      <EmptyState
        icon={Target}
        title="이벤트 데이터가 없어요"
        description="해당 월에 발급된 쿠폰 이벤트가 아직 없어요"
      />
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>이벤트명</TableHead>
          <TableHead>출처</TableHead>
          <TableHead className="text-right">발급 수</TableHead>
          <TableHead className="text-right">재구매율</TableHead>
          <TableHead className="text-right">총 주문금액</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.events.map((event) => (
          <TableRow key={event.eventId} data-testid={testIds.couponPerformance.drilldownRow}>
            <TableCell>
              <Link
                to="/$mallId/coupon/$eventId"
                params={{ mallId: mallId, eventId: event.eventId }}
                className="text-primary hover:underline font-medium"
              >
                {event.eventName}
              </Link>
            </TableCell>
            <TableCell>
              {event.source ? (
                <Badge variant="secondary" className="text-xs">
                  {SOURCE_LABELS[event.source] ?? event.source}
                </Badge>
              ) : (
                <span className="text-muted-foreground text-xs">-</span>
              )}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {event.issuedCount.toLocaleString('ko-KR')}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {event.repurchaseRate.toFixed(1)}%
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {event.totalOrderAmount.toLocaleString('ko-KR')}원
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function CouponPerformancePage() {
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);

  const { data: heatmapData, isLoading: isHeatmapLoading } = usePerformanceHeatmap();
  const { mutate: calculate, isPending: isCalculating } = useCalculatePerformance();

  const rows = heatmapData?.rows ?? [];
  const maxMonthOffset = heatmapData?.maxMonthOffset ?? 0;

  // Compute summary from flat rows
  const months = Array.from(new Set(rows.map((r) => r.issuedMonth))).sort();
  const totalMonths = months.length;

  const m1Rows = rows.filter((r) => r.monthOffset === 1);
  const avgRepurchaseRate =
    m1Rows.length > 0 ? m1Rows.reduce((sum, r) => sum + r.repurchaseRate, 0) / m1Rows.length : 0;

  return (
    <AppLayout pageTitle="쿠폰 성과 분석">
      <div className="space-y-6 max-w-6xl mx-auto">
        <AnimatedContainer delay={0}>
          <div className="flex items-start justify-between">
            <PageHeader
              title="쿠폰 성과 분석"
              description="쿠폰 발급 후 재구매 전환율을 월별로 추적합니다"
              data-testid={testIds.couponPerformance.pageHeader}
            />
            <Button
              onClick={() => calculate({})}
              disabled={isCalculating}
              variant="outline"
              size="sm"
              className="gap-1.5 shrink-0"
              data-testid={testIds.couponPerformance.recalculateButton}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isCalculating ? 'animate-spin' : ''}`} />
              재계산
            </Button>
          </div>
        </AnimatedContainer>

        <AnimatedContainer delay={0.05}>
          <MemberOnlyNotice />
        </AnimatedContainer>

        <AnimatedContainer delay={0.1}>
          <AnalysisGuide title="이 분석은 이렇게 읽어요">
            <p>
              쿠폰을 발급한 뒤, 고객이 실제로 <strong>재구매</strong>로 이어졌는지를 월별로
              추적해요.
            </p>
            <p>
              히트맵의 <strong>M+1, M+2…</strong>는 쿠폰 발급 후 1개월, 2개월 뒤의 재구매율이에요.
              숫자가 높을수록 색이 진해져요.
            </p>
            <div className="mt-3 pt-3 border-t border-border/50">
              <p className="font-medium text-foreground/80">이렇게 활용해 보세요</p>
              <ul className="mt-1.5 space-y-1 text-muted-foreground list-disc list-inside">
                <li>
                  <strong>M+1 재구매율이 높은 쿠폰</strong>은 즉각 효과가 좋은 쿠폰이에요. 비슷한
                  조건으로 더 자주 발급해 보세요.
                </li>
                <li>
                  <strong>M+2, M+3까지 색이 진한 쿠폰</strong>은 장기 충성도에 기여하는 쿠폰이에요.
                  VIP 대상에 집중하면 효과적이에요.
                </li>
                <li>
                  행을 클릭하면 해당 월의 <strong>이벤트별 상세 성과</strong>를 바로 확인할 수
                  있어요.
                </li>
              </ul>
            </div>
          </AnalysisGuide>
        </AnimatedContainer>

        <AnimatedContainer delay={0.2}>
          <div
            className="grid grid-cols-3 gap-4"
            data-testid={testIds.couponPerformance.summaryCards}
          >
            <Card className="py-4" data-testid={testIds.couponPerformance.totalMonths}>
              <CardContent className="flex items-center gap-3">
                <div className="rounded-lg bg-primary/10 p-2">
                  <CalendarRange className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">총 발급 월 수</p>
                  {isHeatmapLoading ? (
                    <Skeleton className="h-7 w-20" />
                  ) : (
                    <p className="text-2xl font-semibold tabular-nums">
                      <AnimatedCount value={totalMonths} />
                      개월
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
            <Card className="py-4" data-testid={testIds.couponPerformance.avgRepurchaseRate}>
              <CardContent className="flex items-center gap-3">
                <div className="rounded-lg bg-signal-green/10 p-2">
                  <Percent className="h-4 w-4 text-signal-green" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">평균 재구매율 (M+1)</p>
                  {isHeatmapLoading ? (
                    <Skeleton className="h-7 w-20" />
                  ) : (
                    <p className="text-2xl font-semibold tabular-nums text-signal-green-text">
                      <AnimatedCount value={avgRepurchaseRate} decimals={1} />%
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
            <Card className="py-4" data-testid={testIds.couponPerformance.analysisPeriod}>
              <CardContent className="flex items-center gap-3">
                <div className="rounded-lg bg-primary/10 p-2">
                  <BarChart3 className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">분석 기간</p>
                  {isHeatmapLoading ? (
                    <Skeleton className="h-7 w-32" />
                  ) : (
                    <p className="text-sm font-medium tabular-nums">
                      {months.length > 0 ? `${months[0]} ~ ${months[months.length - 1]}` : '-'}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </AnimatedContainer>

        <AnimatedContainer delay={0.3}>
          <Card className="py-0 gap-0" data-testid={testIds.couponPerformance.heatmapCard}>
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div>
                <h2 className="text-sm font-semibold">월별 재구매율 히트맵</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  발급 월 행을 클릭하면 이벤트 상세를 확인할 수 있어요
                </p>
              </div>
            </div>
            <div className="p-6">
              <PerformanceHeatmap
                rows={rows}
                maxMonthOffset={maxMonthOffset}
                isLoading={isHeatmapLoading}
                selectedMonth={selectedMonth}
                onSelectMonth={(month) =>
                  setSelectedMonth((prev) => (prev === month ? null : month))
                }
              />
            </div>
          </Card>
        </AnimatedContainer>

        {selectedMonth && (
          <AnimatedContainer delay={0}>
            <Card className="py-0 gap-0" data-testid={testIds.couponPerformance.drilldownCard}>
              <div className="px-6 py-4 border-b">
                <h2 className="text-sm font-semibold">{selectedMonth} 발급 이벤트 상세</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  해당 월에 발급된 쿠폰 이벤트별 성과예요
                </p>
              </div>
              <div className="p-6">
                <DrilldownTable issuedMonth={selectedMonth} />
              </div>
            </Card>
          </AnimatedContainer>
        )}
      </div>
    </AppLayout>
  );
}
